import { FC } from 'react'
import { InputNumber } from 'antd'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { InputNumberProps } from 'antd/lib/input-number'
import { TimeZoneAndLocale } from '../../../types.custom'
import './index.less'
import { Addon } from './Addon'

export type PerInputNumberProps = InputNumberProps & {
  type?: 'currency' | 'percentage' | 'decimalPercentage'
  currency?: string
  timezoneAndLocale?: TimeZoneAndLocale
  step?: number
  width?: string
  percentageHasDecimal?: boolean
  currencyPlaceholder?: boolean
}

const PerInputNumber: FC<PerInputNumberProps> = ({
  type,
  currency,
  timezoneAndLocale,
  step,
  width = '120px',
  percentageHasDecimal = false,
  currencyPlaceholder,
  ...restProps
}) => {
  const { formatCurrencySymbol, laParseUserInput, laFormatUserInput, formatPercentageSymbol } = useAmountAndCurrency(
    timezoneAndLocale ? timezoneAndLocale.locale : undefined,
  )

  const precision = restProps.precision || 2

  return (
    <div className="per-input-number">
      {!type && <InputNumber {...restProps} />}
      {type === 'currency' && currency !== undefined && !currencyPlaceholder && (
        <>
          <InputNumber
            data-testid="per-input-number-currency"
            formatter={(value) => laFormatUserInput(value, 'currency')}
            parser={(value) => laParseUserInput(value, 'currency', precision)}
            step={step ? step : 100}
            style={{ width: width }}
            {...restProps}
          />
          <Addon size={restProps.size || 'default'} symbol={formatCurrencySymbol(currency)} />
        </>
      )}
      {type === 'decimalPercentage' && (
        <>
          <InputNumber
            data-testid="per-input-number-currency"
            formatter={(value) => laFormatUserInput(value)}
            parser={(value) => laParseUserInput(value, 'decimal', precision)}
            step={step ? step : 100}
            style={{ width: width }}
            {...restProps}
          />
          <Addon size={restProps.size || 'default'} symbol={'%'} />
        </>
      )}
      {type === 'currency' && currency !== undefined && currencyPlaceholder && (
        <>
          <InputNumber
            data-testid="per-input-number-currency"
            formatter={(value) => (!!value ? `${laFormatUserInput(value, 'currency')}\u00A0${formatCurrencySymbol(currency)}` : '')}
            parser={(value) => laParseUserInput(value, 'currency', precision)}
            step={step ? step : 100}
            style={{ width: width }}
            {...restProps}
            placeholder={`${restProps.placeholder}\u00A0${formatCurrencySymbol(currency)}`}
          />
        </>
      )}
      {type === 'currency' && currency === undefined && !currencyPlaceholder && (
        <>
          <InputNumber
            data-testid="per-input-number-currency"
            formatter={(value) => laFormatUserInput(value, 'currency')}
            parser={(value) => laParseUserInput(value, 'currency', precision)}
            step={step ? step : 100}
            style={{ width: width }}
            {...restProps}
          />
        </>
      )}
      {type === 'percentage' && (
        <>
          <InputNumber
            data-testid="per-input-number-percentage"
            formatter={(value) => laFormatUserInput(value)}
            parser={(value) => laParseUserInput(value, 'decimal', percentageHasDecimal ? precision : 0)}
            step={step ? step : 10}
            min={0}
            style={{ width: width }}
            {...restProps}
          />
          <Addon size={restProps.size || 'default'} symbol={formatPercentageSymbol()} />
        </>
      )}
    </div>
  )
}

export default PerInputNumber
